import { useState, useEffect } from 'react';
import { Upload, History, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { SimplifiedImportDialog } from './SimplifiedImportDialog';
import { TransactionSummary } from './TransactionSummary';
import { QuickUploadZone } from './QuickUploadZone';
import { ProcessingFeedback } from './ProcessingFeedback';
import { RecentStatementsList } from './RecentStatementsList';
import { BankStatementProcessor } from '../../services/reconciliation/BankStatementProcessor';
import { BankStatement, BankTransaction } from '../../types/bank-reconciliation';
import { useAuth } from '@/hooks/useAuth';
import { useSmartAccountDefaults } from '@/hooks/useSmartAccountDefaults';

type PageMode = 'upload' | 'review' | 'history';

interface ProcessingState {
    isProcessing: boolean;
    progress: number;
    message: string;
    error?: string;
}

export function SimplifiedReconciliationPage() {
    const { user } = useAuth();
    const { getDefaultAccount } = useSmartAccountDefaults();
    const [pageMode, setPageMode] = useState<PageMode>('upload');
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [recentStatements, setRecentStatements] = useState<BankStatement[]>([]);
    const [currentStatement, setCurrentStatement] = useState<BankStatement | null>(null);
    const [currentTransactions, setCurrentTransactions] = useState<BankTransaction[]>([]);
    const [processingState, setProcessingState] = useState<ProcessingState>({
        isProcessing: false,
        progress: 0,
        message: ''
    });

    // Load recent statements on mount
    useEffect(() => {
        if (user) {
            loadRecentStatements();
        }
    }, [user]);

    const loadRecentStatements = async () => {
        if (!user) return;

        try {
            const statements = await BankStatementProcessor.getBankStatements(user.id, 10);
            setRecentStatements(statements);
        } catch (error) {
            console.error('Error loading recent statements:', error);
        }
    };

    // Handle file upload
    const handleFileSelect = async (file: File) => {
        if (!user) return;

        setProcessingState({
            isProcessing: true,
            progress: 5,
            message: 'Verificando contas de pagamento...'
        });

        try {
            toast.loading('Processando arquivo OFX...');

            // Get a valid payment account ID
            const defaultAccountId = await getDefaultAccount('expense');
            if (!defaultAccountId) {
                throw new Error('Nenhuma conta de pagamento encontrada. Por favor, crie uma conta de pagamento antes de importar extratos.');
            }

            setProcessingState({
                isProcessing: true,
                progress: 10,
                message: 'Enviando arquivo...'
            });

            setProcessingState({
                isProcessing: true,
                progress: 30,
                message: 'Analisando estrutura do arquivo...'
            });

            const processingResult = await BankStatementProcessor.processStatement({
                file: file,
                bankCode: '001', // Default to Banco do Brasil, will be auto-detected
                paymentAccountId: defaultAccountId.toString(),
                userId: user.id
            });

            if (!processingResult.success) {
                throw new Error(processingResult.errors?.join(', ') || 'Erro ao processar arquivo');
            }

            setProcessingState({
                isProcessing: true,
                progress: 75,
                message: 'Carregando transações...'
            });

            // Load transactions for the new statement
            const transactions = await BankStatementProcessor.getBankTransactions(
                processingResult.statement.id,
                user.id,
                { limit: 200 }
            );

            setProcessingState({
                isProcessing: true,
                progress: 90,
                message: 'Finalizando...'
            });

            setCurrentStatement(processingResult.statement);
            setCurrentTransactions(transactions);
            setPageMode('review');

            // Reload recent statements
            await loadRecentStatements();

            setProcessingState({
                isProcessing: false,
                progress: 100,
                message: ''
            });

            toast.success(`Arquivo processado com sucesso! ${transactions.length} transações encontradas.`);

        } catch (error) {
            console.error('Error processing file:', error);

            setProcessingState({
                isProcessing: false,
                progress: 0,
                message: '',
                error: error instanceof Error ? error.message : 'Erro ao processar arquivo'
            });

            toast.error('Erro ao processar arquivo. Por favor, tente novamente.');
        } finally {
            toast.dismiss();
        }
    };

    // Handle auto-match
    const handleAutoMatch = async () => {
        if (!currentStatement || !user) return;

        try {
            setProcessingState({
                isProcessing: true,
                progress: 20,
                message: 'Executando conciliação automática...'
            });

            const result = await BankStatementProcessor.autoMatch(currentStatement.id, user.id);

            setProcessingState({
                isProcessing: true,
                progress: 80,
                message: 'Atualizando transações...'
            });

            // Reload transactions to show updated matches
            const updatedTransactions = await BankStatementProcessor.getBankTransactions(
                currentStatement.id,
                user.id,
                { limit: 200 }
            );

            setCurrentTransactions(updatedTransactions);

            setProcessingState({
                isProcessing: false,
                progress: 100,
                message: ''
            });

            toast.success(`Conciliação automática concluída! ${result.matched} transações pareadas.`);

        } catch (error) {
            console.error('Error in auto-match:', error);

            setProcessingState({
                isProcessing: false,
                progress: 0,
                message: '',
                error: error instanceof Error ? error.message : 'Erro na conciliação automática'
            });

            toast.error('Erro na conciliação automática');
        }
    };

    // Handle new import
    const handleNewImport = () => {
        setCurrentStatement(null);
        setCurrentTransactions([]);
        setPageMode('upload');
        setProcessingState({
            isProcessing: false,
            progress: 0,
            message: ''
        });
    };

    // Handle statement selection from history
    const handleStatementSelect = (statement: BankStatement) => {
        setCurrentStatement(statement);
        setPageMode('review');
        // Load transactions for selected statement
        loadStatementTransactions(statement.id);
    };

    const loadStatementTransactions = async (statementId: string) => {
        if (!user) return;

        try {
            const transactions = await BankStatementProcessor.getBankTransactions(
                statementId,
                user.id,
                { limit: 200 }
            );
            setCurrentTransactions(transactions);
        } catch (error) {
            console.error('Error loading transactions:', error);
            toast.error('Erro ao carregar transações');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Conciliação Bancária</h1>
                    <p className="text-muted-foreground">
                        Importe e concilie seus extratos bancários automaticamente
                    </p>
                </div>

                {/* Mode Switcher */}
                <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
                    <Button
                        variant={pageMode === 'upload' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setPageMode('upload')}
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                    </Button>
                    <Button
                        variant={pageMode === 'review' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setPageMode('review')}
                        disabled={!currentStatement}
                    >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Revisão
                    </Button>
                    <Button
                        variant={pageMode === 'history' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setPageMode('history')}
                    >
                        <History className="h-4 w-4 mr-2" />
                        Histórico
                    </Button>
                </div>
            </div>

            {/* Processing Overlay */}
            {(processingState.isProcessing || processingState.error) && (
                <ProcessingFeedback
                    isProcessing={processingState.isProcessing}
                    progress={processingState.progress}
                    message={processingState.message}
                    error={processingState.error}
                />
            )}

            {/* Main Content */}
            <div className="min-h-[600px]">
                {/* Upload Mode */}
                {pageMode === 'upload' && (
                    <div className="space-y-6">
                        <QuickUploadZone
                            onFileSelect={handleFileSelect}
                            isProcessing={processingState.isProcessing}
                            processingProgress={processingState.progress}
                        />

                        {/* Recent Statements Preview */}
                        {recentStatements.length > 0 && (
                            <div>
                                <h2 className="text-xl font-semibold mb-4">Importações Recentes</h2>
                                <RecentStatementsList
                                    statements={recentStatements.slice(0, 3)}
                                    onSelectStatement={handleStatementSelect}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Review Mode */}
                {pageMode === 'review' && currentStatement && (
                    <TransactionSummary
                        statement={currentStatement}
                        transactions={currentTransactions}
                        onAutoMatch={handleAutoMatch}
                        onNewImport={handleNewImport}
                        isProcessing={processingState.isProcessing}
                    />
                )}

                {/* History Mode */}
                {pageMode === 'history' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-semibold mb-2">Histórico de Importações</h2>
                            <p className="text-muted-foreground">
                                Visualize e gerencie todos os extratos importados
                            </p>
                        </div>

                        <RecentStatementsList
                            statements={recentStatements}
                            onSelectStatement={handleStatementSelect}
                        />
                    </div>
                )}
            </div>

            {/* Import Dialog */}
            <SimplifiedImportDialog
                open={showImportDialog}
                onOpenChange={setShowImportDialog}
                onSuccess={(result) => {
                    if (result.success && result.statement && result.transactions) {
                        setCurrentStatement(result.statement);
                        setCurrentTransactions(result.transactions);
                        setPageMode('review');
                    }
                }}
            />
        </div>
    );
}
